import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

const entityLabels = {
  competition: 'Compétitions',
  team: 'Équipes',
  player: 'Joueurs',
  match: 'Matchs',
  gameFact: 'Faits de jeu',
};

const statusConfig = {
  create: { label: 'À créer', color: 'success' },
  update: { label: 'À modifier', color: 'warning' },
  unchanged: { label: 'Déjà à jour', color: 'default' },
  skipped: { label: 'Non importé', color: 'default' },
  blocked: { label: 'Bloqué', color: 'error' },
};

const getEntityHref = (entityType, targetId) => {
  if (!targetId) return null;
  if (entityType === 'competition') return `/sports/competitions/create/${targetId}`;
  if (entityType === 'team') return `/sports/teams/create/${targetId}`;
  if (entityType === 'player') return `/sports/players/create/${targetId}`;
  if (entityType === 'match') return `/match/${targetId}`;
  return null;
};

const formatValue = (value) => {
  if (value === undefined) return '—';
  if (value === null) return 'null';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    const serialized = JSON.stringify(value, null, 2);
    return serialized.length > 1200 ? `${serialized.slice(0, 1200)}…` : serialized;
  } catch {
    return String(value);
  }
};

const ChangeRow = ({ change }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '180px 1fr 1fr' },
      gap: 1,
      py: 0.75,
      '& + &': { borderTop: '1px solid', borderColor: 'divider' },
    }}
  >
    <Typography variant="body2" fontWeight={600}>{change?.field || 'Modification'}</Typography>
    <Box>
      <Typography variant="caption" color="text.secondary">Actuel</Typography>
      <Typography component="pre" variant="caption" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {formatValue(change?.current)}
      </Typography>
    </Box>
    <Box>
      <Typography variant="caption" color="text.secondary">Après import</Typography>
      <Typography component="pre" variant="caption" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {formatValue(change?.incoming)}
      </Typography>
      {change?.message && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {change.message}
        </Typography>
      )}
    </Box>
  </Box>
);

const DryRunItem = ({ item }) => {
  const [expanded, setExpanded] = useState(false);
  const config = statusConfig[item?.status] || statusConfig.unchanged;
  const href = getEntityHref(item?.entityType, item?.targetId);
  const hasDetails = Boolean(
    item?.changes?.length || item?.note || item?.blocker || item?.context || item?.targetId || item?.sourceId
  );

  return (
    <Box sx={{ py: 0.75, '& + &': { borderTop: '1px solid', borderColor: 'divider' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip size="small" label={config.label} color={config.color} variant={['unchanged', 'skipped'].includes(item?.status) ? 'outlined' : 'filled'} />
        <Typography variant="body2" fontWeight={600} sx={{ flex: '1 1 280px' }}>
          {item?.name || item?.sourceId || 'Entité'}
        </Typography>
        {hasDetails && (
          <Button
            size="small"
            variant="text"
            startIcon={expanded ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? 'Masquer' : 'Détails'}
          </Button>
        )}
      </Box>

      {hasDetails && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Stack spacing={1.25}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">Source id : {item?.sourceId || '—'}</Typography>
                <Typography variant="caption" color="text.secondary">Cible id : {item?.targetId || 'création'}</Typography>
                <Typography variant="caption" color="text.secondary">Provider cible : {item?.targetProvider || '—'}</Typography>
              </Box>

              {(item?.identity?.thirdPartyId || item?.identity?.thirdPartyEntityId) && (
                <Typography variant="body2">
                  <strong>Identité :</strong> {item?.identity?.thirdPartyId || '—'} / {item?.identity?.thirdPartyEntityId || '—'}
                </Typography>
              )}

              {item?.context?.matchName && (
                <Typography variant="body2"><strong>Match :</strong> {item.context.matchName}</Typography>
              )}

              {item?.note && <Alert severity="info">{item.note}</Alert>}
              {item?.blocker?.message && <Alert severity="error">{item.blocker.message}</Alert>}

              {href && (
                <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, width: 'fit-content' }}>
                  Ouvrir l'entité cible
                  <OpenInNewOutlinedIcon fontSize="inherit" />
                </Link>
              )}

              {item?.changes?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Modifications prévues</Typography>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1 }}>
                    {item.changes.map((change, index) => (
                      <ChangeRow key={`${change?.field || 'change'}-${index}`} change={change} />
                    ))}
                  </Box>
                </Box>
              )}
            </Stack>
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const SummaryChips = ({ summary }) => (
  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
    <Chip label={`${summary?.create || 0} création(s)`} color="success" variant="outlined" />
    <Chip label={`${summary?.update || 0} modification(s)`} color="warning" variant="outlined" />
    <Chip label={`${summary?.unchanged || 0} déjà à jour`} variant="outlined" />
    {(summary?.skipped || 0) > 0 && <Chip label={`${summary?.skipped || 0} non importé(s)`} variant="outlined" />}
    <Chip label={`${summary?.blocked || 0} blocage(s)`} color={summary?.blocked ? 'error' : 'default'} variant="outlined" />
  </Stack>
);

const GameFactStrategySummary = ({ summary }) => {
  if (!summary?.strategy) return null;

  const replace = summary.strategy === 'replace';

  return (
    <Alert severity={replace ? 'warning' : 'info'}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Faits de jeu — {replace ? 'Remplacement sur les Matchs existants' : 'Aucune écriture sur les Matchs existants'}
      </Typography>
      {replace ? (
        <Stack spacing={0.25}>
          <Typography variant="body2">
            • {summary.existingTargetMatches || 0} Match(s) existent déjà en cible.
          </Typography>
          <Typography variant="body2">
            • {summary.existingTargetGameFactsToDelete || 0} fait(s) de jeu actuellement en cible seront remplacés.
          </Typography>
          <Typography variant="body2">
            • {summary.snapshotGameFactsToCreate || 0} fait(s) du snapshot seront créés après remapping.
          </Typography>
          {(summary.existingTargetMatchesCleared || 0) > 0 && (
            <Typography variant="body2">
              • {summary.existingTargetMatchesCleared} Match(s) cible seront laissés sans fait de jeu car le snapshot n'en contient aucun.
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Pour les Matchs à créer, les faits de jeu du snapshot sont simplement créés. Aucun matching GameFact par thirdPartyId n'est utilisé.
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={0.25}>
          <Typography variant="body2">
            • {summary.snapshotGameFactsSkipped || 0} fait(s) du snapshot ne seront pas importés sur les Matchs déjà présents.
          </Typography>
          <Typography variant="body2">
            • {summary.snapshotGameFactsToCreate || 0} fait(s) seront tout de même créés pour les {summary.newTargetMatches || 0} Match(s) à créer.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Les faits de jeu déjà présents sur les Matchs existants restent strictement inchangés.
          </Typography>
        </Stack>
      )}
    </Alert>
  );
};

const EnvironmentImportDryRunReport = ({ result }) => {
  if (!result) return null;

  return (
    <CardLike>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h6">5. Rapport de pré-check</Typography>
          <Typography variant="body2" color="text.secondary">
            Comparaison avec la base de l'environnement cible. Aucune écriture n'a été effectuée.
          </Typography>
        </Box>

        <Alert severity={result.valid ? 'success' : 'error'}>
          {result.valid
            ? 'Le plan d’import ne contient aucun blocage. Tu peux vérifier les créations et modifications prévues avant que l’on branche l’import réel.'
            : 'Le plan contient des blocages. Aucun import ne devra être autorisé tant qu’ils ne sont pas résolus.'}
        </Alert>

        <SummaryChips summary={result.summary} />

        <GameFactStrategySummary summary={result.gameFacts} />

        {result.blockers?.length > 0 && (
          <Alert severity="error">
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Blocages</Typography>
            {result.blockers.slice(0, 20).map((blocker, index) => (
              <Typography variant="body2" key={`${blocker?.type}-${index}`}>• {blocker?.message || blocker?.type}</Typography>
            ))}
            {result.blockers.length > 20 && (
              <Typography variant="body2">… et {result.blockers.length - 20} autre(s).</Typography>
            )}
          </Alert>
        )}

        {result.warnings?.length > 0 && (
          <Alert severity="warning">
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Avertissements</Typography>
            {result.warnings.map((warning, index) => (
              <Typography variant="body2" key={`${warning?.type}-${index}`}>• {warning?.message || warning?.type}</Typography>
            ))}
          </Alert>
        )}

        <Divider />

        <Stack spacing={1.5}>
          {(result.providers || []).map((providerReport) => (
            <Accordion key={providerReport.provider} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ width: '100%', pr: 2 }}>
                  <Typography fontWeight={700}>{providerReport.provider}</Typography>
                  <SummaryChips summary={providerReport.summary} />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {Object.entries(providerReport.entities || {}).map(([entityType, section]) => (
                    <Accordion key={`${providerReport.provider}-${entityType}`} variant="outlined" disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ width: '100%', pr: 2 }}>
                          <Typography fontWeight={600}>{entityLabels[entityType] || entityType}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {section?.summary?.create || 0} à créer · {section?.summary?.update || 0} à modifier · {section?.summary?.unchanged || 0} à jour · {section?.summary?.skipped || 0} non importé(s) · {section?.summary?.blocked || 0} bloqué(s)
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {(section?.items || []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            Aucune action à afficher pour ce type d'entité.
                          </Typography>
                        ) : (
                          <Box>
                            {section.items.map((item, index) => (
                              <DryRunItem key={`${item?.sourceId || index}-${item?.targetProvider}-${index}`} item={item} />
                            ))}
                          </Box>
                        )}

                        {section?.truncated && (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            {section.hiddenActionItems} action(s) supplémentaire(s) ne sont pas affichée(s) pour préserver la fluidité de la page.
                          </Alert>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Stack>
    </CardLike>
  );
};

const CardLike = ({ children }) => (
  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: { xs: 2, md: 3 }, bgcolor: 'background.paper' }}>
    {children}
  </Box>
);

export default EnvironmentImportDryRunReport;
